import { getURLSearchParams } from '@web-platform/shared-banana-utils-frontend/src/helpers/getURLSearchParams';
import { getUserDevice, getUserIdentifiers } from '@web-platform/shared-universal-redux/src/state';
import { getCulture } from '@web-platform/shared-universal-utils-frontend-localisation';
import { useSelector } from 'react-redux';
import { getPreferences, PREFERENCES } from 'saddlebag-tracking-preferences';

import type { AdsCapabilityVariables } from '@web-platform/shared-universal-types/src/network/banana/requestTypes/adsCapabilityRequestTypes';

export type AdsCapabilityCommonVariables = Pick<
  AdsCapabilityVariables,
  | 'market'
  | 'locale'
  | 'currency'
  | 'platform'
  | 'advertsConsentGiven'
  | 'utid'
  | 'viewId'
  | 'authenticated'
  | 'deviceGuid'
  | 'experimentAllocationId'
  | 'creativeId'
>;

/**
 * Hook to assemble AdsCapability variables that are common across all pages.
 * Returns universal variables: culture, platform, consent, identifiers, device/experiment tracking,
 * and ad testing override (creativeId/sponsoredPreviewId).
 *
 * Page-specific adapters use this as a base and add their own variables (cabinClass, experience, etc.)
 * @returns {AdsCapabilityCommonVariables} Common ADS Capability variables
 */
export const useAdsCapabilityCommonVariables = (): AdsCapabilityCommonVariables => {
  const { currency, locale, market } = getCulture();
  const userIdentifiers = useSelector(getUserIdentifiers);
  const { isMobilePhone } = useSelector(getUserDevice);
  const sponsoredPreviewId = getURLSearchParams().get('sponsoredPreviewId');

  // Build platform: PLATFORM_DESKTOP for desktop, PLATFORM_MOBILE for mobile/tablet
  const platform: AdsCapabilityVariables['platform'] = isMobilePhone
    ? 'PLATFORM_MOBILE'
    : 'PLATFORM_DESKTOP';

  const trackingPreferences = getPreferences(market);
  const advertsConsentGiven = trackingPreferences[PREFERENCES.ADVERTS];

  return {
    market,
    locale,
    currency,
    platform,
    advertsConsentGiven,
    utid: userIdentifiers?.utid ?? '',
    viewId: userIdentifiers?.viewId ?? undefined,
    authenticated: userIdentifiers?.isLoggedIn ?? undefined,
    // deviceGuid and experimentAllocationId left undefined per spike outcome
    // but available for future enhancement
    deviceGuid: undefined,
    experimentAllocationId: undefined,
    // Ad testing override: available on all pages for testing specific creatives
    creativeId: sponsoredPreviewId || undefined,
  };
};
